import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Palette, Target } from 'lucide-react';
import { PSEO_GENRES, PSEO_TOOLS } from '../data/pseoData';

/**
 * Related PSEO Pages Component for Internal Linking
 * Improves SEO by distributing page authority and enhancing crawlability
 */
export const RelatedPSEOPages = ({ currentGenre, currentTool, maxLinks = 6 }) => {
  const getRelatedPages = () => {
    const related = [];
    
    // Add same genre, different tools
    if (currentGenre && PSEO_GENRES[currentGenre]) {
      Object.keys(PSEO_TOOLS).forEach(tool => {
        if (tool !== currentTool) {
          related.push({
            url: `/${currentGenre}-book-${tool.replace('_', '-')}.html`,
            title: `${PSEO_GENRES[currentGenre].title} ${PSEO_TOOLS[tool].title}`,
            description: `${PSEO_TOOLS[tool].description} for ${currentGenre} books`,
            type: 'same-genre'
          });
        }
      });
    }
    
    // Add same tool, different genres  
    if (currentTool && PSEO_TOOLS[currentTool]) {
      Object.keys(PSEO_GENRES).forEach(genre => {
        if (genre !== currentGenre) {
          related.push({
            url: `/${genre}-book-${currentTool.replace('_', '-')}.html`,
            title: `${PSEO_GENRES[genre].title} ${PSEO_TOOLS[currentTool].title}`,
            description: `${PSEO_TOOLS[currentTool].description} for ${genre} books`,
            type: 'same-tool'
          });
        }
      });
    }
    
    // Prioritize same-genre links, then shuffle and limit
    const sameGenre = related.filter(p => p.type === 'same-genre').slice(0, 3);
    const sameTool = related.filter(p => p.type === 'same-tool').slice(0, 3);
    
    return [...sameGenre, ...sameTool].slice(0, maxLinks);
  };

  const relatedPages = getRelatedPages();
  
  if (relatedPages.length === 0) return null;

  return (
    <section className="py-16 px-4 bg-gray-900/30">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-white text-center mb-12">
          Related Guides & Tools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {relatedPages.map((page, index) => (
            <Link
              key={index}
              to={page.url}
              className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-all duration-300 transform hover:scale-105 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                    {page.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {page.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center text-purple-400 text-sm">
                <span>Learn more</span>
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * PSEO Navigation Breadcrumbs for better internal linking
 */
export const PSEOBreadcrumbs = ({ genre, tool, genreData, toolData }) => {
  if (!genre || !tool) return null;

  return (
    <nav className="py-4 px-4" aria-label="Breadcrumb">
      <div className="max-w-6xl mx-auto">
        <ol className="flex items-center space-x-2 text-sm text-gray-400">
          <li>
            <Link to="/" className="hover:text-purple-400 transition-colors">
              Home
            </Link>
          </li>
          <li>
            <span className="mx-2">/</span>
            <span className="capitalize">
              {genreData?.title || genre} Books
            </span>
          </li>
          <li>
            <span className="mx-2">/</span>
            <span className="text-white font-medium">
              {genreData?.title} {toolData?.title}
            </span>
          </li>
        </ol>
      </div>
    </nav>
  );
};

/**
 * Strategic Internal Links for Homepage
 */
export const HomepagePSEOLinks = () => {
  const topGenres = ['romance', 'fantasy', 'mystery', 'business'];
  const topTools = ['cover-generator', 'writing-guide', 'book-creator'];
  
  // Generate URLs that match the actual routes in App.js
  const generateURL = (genre, tool) => {
    return `/${genre}-book-${tool.replace("_", "-")}.html`;
  };
  
  const featuredPages = topGenres.flatMap(genre => 
    topTools.map(tool => {
      // Add error handling for missing data
      const genreData = PSEO_GENRES[genre];
      const toolData = PSEO_TOOLS[tool];
      
      if (!genreData || !toolData) {
        console.warn(`Missing data for ${genre} + ${tool}`);
        return null;
      }
      
      return {
        url: generateURL(genre, tool),
        title: `${genreData.title} ${toolData.title}`,
        description: `${toolData.description} for ${genre} books`,
        genre,
        tool,
        icon: tool === 'cover-generator' ? Palette : 
              tool === 'writing-guide' ? BookOpen : Target
      };
    })
  ).filter(Boolean).slice(0, 12);

  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-white text-center mb-4">
          Specialized Guides for Every Genre
        </h2>
        <p className="text-gray-300 text-center mb-12 max-w-3xl mx-auto">
          Discover our comprehensive collection of AI-powered tools and guides designed specifically for different book genres.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {featuredPages.map((page, index) => {
            const Icon = page.icon;
            return (
              <Link
                key={index}
                to={page.url}
                className="bg-gray-800/50 rounded-xl p-6 hover:bg-gray-700/50 transition-all duration-300 transform hover:scale-105 group border border-purple-500/20 hover:border-purple-500/50"
              >
                <div className="flex items-center mb-4">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-3 mr-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-purple-400 transition-colors">
                      {page.title}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {page.description}
                </p>
                <div className="flex items-center text-purple-400 text-sm">
                  <span>Explore tools</span>
                  <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedPSEOPages;